import React, { useState, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useClickOutside } from '@/shared/hooks/useClickOutside';
import { getContentLanguageLabel } from '@/shared/utils/languageUtils';
import chevronIcon from '@/assets/svg/chevron_icon.svg';
import { ModalCloseButton } from './ModalCloseButton';
import { DialogShell } from './DialogShell';

interface PreviewModalProps {
    isOpen: boolean;
    onClose: () => void;
    previewUrl: string;
    selectedLanguage?: string;
    onLanguageChange?: (lang: string) => void;
    availableLanguages?: string[];
}

export const PreviewModal: React.FC<PreviewModalProps> = ({
    isOpen,
    onClose,
    previewUrl,
    selectedLanguage = 'fr',
    onLanguageChange,
    availableLanguages = ['fr'],
}) => {
    const { t } = useTranslation();
    const [languageDropdownOpen, setLanguageDropdownOpen] = useState(false);
    const languageDropdownRef = useRef<HTMLDivElement>(null);
    const titleId = 'preview-modal-title';

    useClickOutside(languageDropdownRef, () => {
        if (languageDropdownOpen) {
            setLanguageDropdownOpen(false);
        }
    });

    const iframeUrl = useMemo(() => {
        try {
            const url = new URL(previewUrl);
            url.searchParams.set('preview', 'true');
            url.searchParams.set('lang', selectedLanguage);
            return url.toString();
        } catch {
            const separator = previewUrl.includes('?') ? '&' : '?';
            return `${previewUrl}${separator}preview=true&lang=${selectedLanguage}`;
        }
    }, [previewUrl, selectedLanguage]);

    return (
        <DialogShell
            isOpen={isOpen}
            onClose={onClose}
            titleId={titleId}
            overlayClassName="preview-modal-overlay"
            dialogClassName="preview-modal-content"
            initialFocusSelector=".close-preview-btn, .modal-close-button"
        >
            <h2 id={titleId} className="sr-only">
                {t('exhibitions.closePreview', 'Fermer la prévisualisation')}
            </h2>
            <div className="preview-header">
                {availableLanguages.length > 1 && onLanguageChange && (
                    <div className="preview-language-selector" ref={languageDropdownRef}>
                        <div className="dropdown">
                            <button
                                type="button"
                                className="dropdown-button"
                                onClick={() => setLanguageDropdownOpen(!languageDropdownOpen)}
                                aria-expanded={languageDropdownOpen}
                                aria-haspopup="listbox"
                            >
                                <span>{getContentLanguageLabel(selectedLanguage)}</span>
                                <img
                                    src={chevronIcon}
                                    alt=""
                                    className={`chevron-icon ${languageDropdownOpen ? 'rotated' : ''}`}
                                />
                            </button>
                            {languageDropdownOpen && (
                                <div className="dropdown-menu" role="listbox">
                                    {availableLanguages.map((lang) => (
                                        <button
                                            key={lang}
                                            type="button"
                                            role="option"
                                            aria-selected={selectedLanguage === lang}
                                            onClick={() => {
                                                onLanguageChange(lang);
                                                setLanguageDropdownOpen(false);
                                            }}
                                            className={selectedLanguage === lang ? 'active' : ''}
                                        >
                                            {getContentLanguageLabel(lang)}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                )}
                <div className="close-preview-btn-container">
                    <ModalCloseButton onClick={onClose} className="close-preview-btn" />
                    <span className="close-text">{t('exhibitions.closePreview', 'Fermer la prévisualisation')}</span>
                </div>
            </div>
            <div className="phone-container">
                <div className="phone-frame">
                    <div className="phone-screen">
                        <iframe
                            src={iframeUrl}
                            className="preview-iframe"
                            title="Preview"
                            allow="geolocation"
                            sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox"
                            key={iframeUrl}
                        />
                    </div>
                </div>
            </div>
        </DialogShell>
    );
};
